import { createContext, useContext, useMemo } from 'react';
import { Editor, Element, Node, Path } from 'slate';
import { useSlate } from 'slate-react';
import { useEditorMode } from 'app/state/contexts/EditorContext';
import { CoverageRule, getHeadingLevel, hasCoverage, isReferenceTable } from 'slate/extensions/coverage';

interface CoverageScopeValue {
  scopeUuids: Set<string>;
  missingUuids: Set<string>;
  anchorUuid: string | null;
  rule: CoverageRule | null;
  anchorsVisible: boolean;
}

const EMPTY: CoverageScopeValue = { scopeUuids: new Set(), missingUuids: new Set(), anchorUuid: null, rule: null, anchorsVisible: false };

const Context = createContext<CoverageScopeValue>(EMPTY);

const CONTAINER_TYPES = new Set(['page', 'layout', 'wrapper']);

const isContainerNode = (node: Node): boolean => {
  if (!Element.isElement(node)) return false;
  if (CONTAINER_TYPES.has((node as { type: string }).type)) return true;
  if (node.children.length === 0) return false;
  return node.children.every((c) => Element.isElement(c));
};

const blockUuid = (node: Node): string | null => {
  if (!Element.isElement(node)) return null;
  return (node as { uuid?: string }).uuid ?? null;
};

const containsTag = (node: Node): boolean => {
  if (Element.isElement(node)) {
    if ((node as { type: string }).type === 'tag') return true;
    const tags = (node as { tags?: unknown }).tags;
    if (Array.isArray(tags) && tags.length > 0) return true;
    return node.children.some(containsTag);
  }
  return false;
};

const nodeText = (node: Node): string => {
  if (!Element.isElement(node)) return (node as { text?: string }).text ?? '';
  return node.children.map(nodeText).join('');
};

const collectScopeFrom = (flat: Array<{ node: Node; path: Path }>, headingIdx: number, scopeUuids: Set<string>, missingUuids: Set<string>): void => {
  const headingNode = flat[headingIdx].node;
  const headingLevel = getHeadingLevel(headingNode);
  if (headingLevel === null) return;
  const rule: CoverageRule | null = hasCoverage(headingNode) ? headingNode.coverage.rule : null;

  const anchor = blockUuid(headingNode);
  if (anchor) scopeUuids.add(anchor);

  for (let i = headingIdx + 1; i < flat.length; i++) {
    const { node } = flat[i];
    const nextLevel = getHeadingLevel(node);
    const empty = nodeText(node).trim() === '';

    if (nextLevel !== null && nextLevel <= headingLevel && !empty) break;

    const u = blockUuid(node);
    if (!u) continue;

    const isTable = isReferenceTable(node);
    if (!empty && !isTable) scopeUuids.add(u);

    if (rule?.kind === 'strict') {
      if (nextLevel !== null) continue; // sub-headings don't need tags
      if (isTable) continue; // tables have their own marker
      if (empty) continue; // empty paragraphs skipped
      if (!containsTag(node)) missingUuids.add(u);
    }
  }
};

const findEnclosingHeadingPath = (editor: Editor): Path | null => {
  if (!editor.selection) return null;
  let cursor: Path = Editor.path(editor, editor.selection);
  while (cursor.length > 0) {
    try {
      const [node] = Editor.node(editor, cursor);
      if (getHeadingLevel(node) !== null) return cursor;
    } catch {
      return null;
    }
    cursor = Path.parent(cursor);
  }
  return null;
};

export const computeScope = (editor: Editor, subMode: 'tag' | 'mark-coverage'): CoverageScopeValue => {
  if (subMode !== 'mark-coverage') return EMPTY;

  const flat: Array<{ node: Node; path: Path }> = Array.from(
    Editor.nodes(editor, {
      at: [],
      match: (n) => Element.isElement(n) && !isContainerNode(n) && Editor.isBlock(editor, n),
    })
  ).map(([node, path]) => ({ node, path }));

  const scopeUuids = new Set<string>();
  const missingUuids = new Set<string>();

  // Persistent: every marked heading contributes its scope.
  let anchorUuid: string | null = null;
  let rule: CoverageRule | null = null;
  for (let i = 0; i < flat.length; i++) {
    const { node } = flat[i];
    if (getHeadingLevel(node) === null) continue;
    if (!hasCoverage(node)) continue;
    if (anchorUuid === null) {
      anchorUuid = blockUuid(node);
      rule = node.coverage.rule;
    }
    collectScopeFrom(flat, i, scopeUuids, missingUuids);
  }

  // Preview: cursor inside an unmarked heading shows what it would cover.
  const headingPath = findEnclosingHeadingPath(editor);
  if (headingPath) {
    const idx = flat.findIndex((e) => Path.equals(e.path, headingPath));
    if (idx !== -1 && !hasCoverage(flat[idx].node)) {
      collectScopeFrom(flat, idx, scopeUuids, missingUuids);
      if (anchorUuid === null) anchorUuid = blockUuid(flat[idx].node);
    }
  }

  return { scopeUuids, missingUuids, anchorUuid, rule, anchorsVisible: true };
};

export const CoverageScopeProvider = ({ children }: { children: React.ReactNode }) => {
  const editor = useSlate();
  const { taggingSubMode, mode } = useEditorMode();
  const effectiveSubMode = mode === 'tagging' ? taggingSubMode : 'tag';

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const value = useMemo(() => computeScope(editor, effectiveSubMode), [editor, editor.children, editor.selection, effectiveSubMode]);

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useCoverageScope = () => useContext(Context);
